// Blast card on the deal page (routes/blast_network.py, app/blast_network.py).
// Zach, 2026-11-02: every sell-side goes out to the buyer network on the
// PB/PEG blast unless the owner limits it. The card holds the two limits:
//   * the switch — off keeps the deal off the broadcasts entirely;
//   * the do-not-contact list — firms (and their portfolio companies) the
//     blast must not reach. The deal's own buyer list is always held out.
// The Blast page (views/blast.jsx) shows the resulting count before a push.

const BnSwitch = ({ on, disabled, onChange }) => {
  // Keyboard focus gets its own ring (inline styles can't say :focus-visible).
  const [ring, setRing] = React.useState(false);
  return (
    <button type="button" role="switch" aria-checked={on} aria-label="Blast to buyer network" disabled={disabled}
      title={on ? 'Goes out on the PB/PEG broadcasts. Turn off to keep this deal off them.' : 'Kept off the PB/PEG broadcasts'}
      onClick={() => onChange(!on)}
      onFocus={(e) => setRing(!!(e.currentTarget.matches && e.currentTarget.matches(':focus-visible')))}
      onBlur={() => setRing(false)}
      style={{ width: 30, height: 17, borderRadius: 999, padding: 1.5, border: 'none', flexShrink: 0,
               cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
               outline: ring ? '2px solid var(--accent-text)' : 'none', outlineOffset: 2,
               background: on ? 'var(--accent-text)' : 'var(--border-strong)', transition: 'background .15s' }}>
      <span style={{ display: 'block', width: 14, height: 14, borderRadius: 999, background: 'var(--surface)',
                     transform: on ? 'translateX(13px)' : 'none', transition: 'transform .15s', boxShadow: '0 1px 2px rgba(0,0,0,.15)' }} />
    </button>
  );
};

const bnPlural = (n, one, many) => `${n} ${n === 1 ? one : many || one + 's'}`;

const BlastNetworkCard = ({ deal, canWrite, variant = 'rail', sectionId = 'deal.blast' }) => {
  const dealId = deal && deal.id;
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [pick, setPick] = React.useState('');
  const load = React.useCallback(() => {
    if (!dealId || !window.AtlasApi.blastNetwork) return;
    window.AtlasApi.blastNetwork(dealId).then((r) => { setData(r); setErr(null); })
      .catch((e) => setErr(String((e && e.message) || e)));
  }, [dealId]);
  React.useEffect(() => { load(); }, [load]);
  if (!dealId || (deal.type && deal.type === 'buyer_buyside')) return null;
  if (data && !data.applies) return null;

  const run = async (fn, failTitle) => {
    setBusy(true);
    try { setData(await fn()); }
    catch (e) { window.pushToast && window.pushToast({ type: 'error', title: failTitle, body: String((e && e.message) || e) }); }
    finally { setBusy(false); }
  };
  const setOn = (on) => run(() => window.AtlasApi.blastNetworkSet(dealId, on), 'Could not change the blast setting');
  const add = (companyId) => {
    setPick('');
    if (!companyId) return;
    run(() => window.AtlasApi.blastDncAdd(dealId, { company_id: companyId }), 'Could not add the firm');
  };
  const togglePortfolio = (d) => run(() => window.AtlasApi.blastDncUpdate(dealId, d.id, { include_portfolio: !d.include_portfolio }), 'Could not update the firm');
  const remove = (d) => run(() => window.AtlasApi.blastDncRemove(dealId, d.id), 'Could not remove the firm');

  const on = data ? data.blast_to_network : true;
  const dnc = (data && data.dnc) || [];
  const preview = !data ? (err ? 'Unavailable' : '…')
    : !on ? 'Off'
    : ['On', dnc.length ? bnPlural(dnc.length, 'do-not-contact firm') : null].filter(Boolean).join(' · ');
  const go = (cid) => window.AtlasNav && window.AtlasNav.goToCompany && window.AtlasNav.goToCompany(cid);
  const line = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px', minHeight: 28, fontSize: 12 };
  const muted = { color: 'var(--text-3)' };

  return (
    <window.Section variant={variant} title="Blast" collapsible id={sectionId} preview={preview}
      action={data ? <BnSwitch on={on} disabled={!canWrite || busy} onChange={setOn} /> : null}>
      {err && !data && <div style={{ ...line, color: 'var(--rose-fg)' }}>{err}</div>}
      {!data && !err && <div style={{ ...line, ...muted }}>Loading…</div>}
      {data && !on && <div style={{ ...line, ...muted }}>Kept off the PB/PEG broadcasts</div>}
      {data && on && (
        <div style={{ padding: '4px 0 8px' }}>
          <div style={line} title="Firms on this deal's buyer list are already being worked directly, so the blast never reaches them">
            <span style={{ flex: 1, minWidth: 0, ...muted }}>Buyer list held out</span>
            <span className="num" style={muted}>{bnPlural(data.buyer_list_firms || 0, 'firm')}</span>
          </div>
          {dnc.map((d) => (
            <div key={d.id} style={{ padding: '4px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button type="button" onClick={() => go(d.company_id)}
                  title={[d.name, d.domain, d.added_by ? `Added by ${d.added_by}` : null, d.note].filter(Boolean).join(' · ')}
                  style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                           color: 'var(--text)', fontWeight: 500, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.name}
                </button>
                {canWrite && (
                  <button type="button" aria-label={`Remove ${d.name} from do-not-contact`} title="Remove from do-not-contact"
                    disabled={busy} onClick={() => remove(d)}
                    style={{ flexShrink: 0, background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--text-4)', lineHeight: 0 }}>
                    {window.Icon && window.Icon.Close ? <window.Icon.Close size={11} /> : '×'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, fontSize: 11, ...muted }}>
                <button type="button" aria-pressed={d.include_portfolio} disabled={!canWrite || busy} onClick={() => togglePortfolio(d)}
                  title={d.include_portfolio ? 'Portfolio companies held out too. Click to hold out the firm only.' : 'Firm only. Click to hold out its portfolio companies too.'}
                  style={{ flexShrink: 0, fontSize: 11, padding: '0 5px', borderRadius: 4, cursor: canWrite ? 'pointer' : 'default',
                           border: '1px solid var(--border)', background: d.include_portfolio ? 'var(--surface-2)' : 'transparent',
                           color: d.include_portfolio ? 'var(--text-2)' : 'var(--text-4)', textDecoration: d.include_portfolio ? 'none' : 'line-through' }}>
                  {`+${d.portfolio_count} portfolio`}
                </button>
                <span className="num" title="Atlas contacts at the firm and the companies held out with it">
                  {bnPlural(d.contact_count, 'contact')}
                </span>
              </div>
            </div>
          ))}
          {canWrite && window.Combobox && (
            <div style={{ padding: '4px 14px 0' }}>
              <window.Combobox value={pick} onChange={(e) => add(e.target.value)} loadOptions={window.searchCompanyOptions}
                placeholder="Add do-not-contact firm…" disabled={busy} dense />
            </div>
          )}
        </div>
      )}
    </window.Section>
  );
};

window.BlastNetworkCard = BlastNetworkCard;
